/**
 * LOD1 TIN as the interactive raycast collider (PRD R5).
 *
 * Hard constraint #1: mesh raycasts are interactive feedback only — the
 * analytical DEM raster (owned by raypath/) is the only source of LOS
 * truth. This class backs walk-mode ground lock, the coordinate readout's
 * live elevation, and any other on-screen "what does the mesh say" value.
 * It must never be presented as, or substituted for, an LOS result.
 *
 * Spatial index (issue #80): tiles are indexed into a 1 km grid keyed by
 * local viewer-space cell, so a ground sample raycasts only the tile(s) that
 * can cover the point (the covering cell, plus its 8 neighbours as a fallback
 * on a fringe/float miss) instead of every loaded tile. The single covering
 * tile is then raycast through a three-mesh-bvh bounds tree (built per tile in
 * addTile, disposed in removeTile) so the cast is O(log n) over its ~8000
 * triangles rather than a linear scan. The returned elevation is unchanged:
 * acceleratedRaycast is documented to return results identical to the built-in
 * raycast, and the regression-lock test asserts that against a brute-force
 * scan. This removes the ~250 ms periodic stall a moving camera hit once the
 * whole 10 km horizon had streamed in. All containment stays in local space,
 * so the north = -z orientation never opens a seam an AC-N2 test would catch.
 */
import { BufferGeometry, DoubleSide, Material, Mesh, Raycaster, Vector3, type Intersection } from 'three';
import {
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
} from 'three-mesh-bvh';

// Install the three-mesh-bvh accelerated raycast once, at module load. Only
// geometries this collider calls computeBoundsTree() on gain a boundsTree; for
// every other mesh acceleratedRaycast falls back to the stock raycast with
// identical results, so this global patch never changes non-collider raycasts.
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;

/** Cast from comfortably above the highest plausible terrain in the AOI. */
const RAYCAST_ORIGIN_Y = 100000;
const DOWN = new Vector3(0, -1, 0);

/**
 * Grid cell size (metres). The manifest's LOD1 tiles are uniform 1 km squares,
 * so with the grid phase anchored to the first registered tile each tile lands
 * in exactly one cell and a point's covering cell + 8 neighbours bounds every
 * raycast at 9 meshes. A tile whose bounds are larger than one cell simply
 * registers into every cell it overlaps and is still found — the index is
 * derived from each tile's actual bounds, never assumed to be exactly one cell.
 */
const CELL_SIZE_M = 1000;

/** Nudge so a tile whose max edge sits exactly on a cell line does not spill
 * into the next (higher) cell. Bounds are metre-scale, so this is well below
 * any real coordinate resolution. */
const EDGE_EPS = 1e-6;

const NEIGHBOUR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

/** Axis-aligned bounds of a tile in local viewer-space (x, z). */
export interface ColliderTileBounds {
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

interface TileEntry {
  mesh: Mesh;
  /** Every grid cell key this tile's bounds overlap. */
  cellKeys: string[];
}

export class TerrainCollider {
  private readonly tiles = new Map<string, TileEntry>();
  /** Reverse index: cell key -> (tile id -> mesh). Keyed by id so a duplicate
   * addTile or a removeTile updates cleanly with no stale mesh left behind. */
  private readonly grid = new Map<string, Map<string, Mesh>>();
  private readonly raycaster = new Raycaster();
  // sampleGround runs on the render loop (orbit/fly camera floor, walk-mode
  // ground lock, boot-intro ground check), so its per-call working state is
  // hoisted here and reused — repo performance rule: no per-frame allocation.
  // Raycaster.set() copies the origin, so reusing one Vector3 is safe; the
  // visited set and hit buffer are cleared at the start of each use.
  private readonly rayOrigin = new Vector3();
  private readonly visitedScratch = new Set<Mesh>();
  private readonly hitScratch: Intersection[] = [];
  private tilesGeneration = 0;
  /** Grid phase, taken from the first registered tile's min corner so cell keys
   * are derived from real data rather than a hard-coded manifest offset. Null
   * until the first tile registers. */
  private gridOriginX: number | null = null;
  private gridOriginZ = 0;

  /** Increments whenever the tile set actually changes. Lets callers skip
   * ground re-sampling entirely on ticks where no tile loaded or unloaded
   * (perf fix for the walls/water freeze cycle). */
  get generation(): number {
    return this.tilesGeneration;
  }

  /**
   * Registers a tile mesh as collider geometry, indexed by the 1 km grid
   * cell(s) its bounds occupy. Forces the mesh's material(s) to DoubleSide as a
   * defence-in-depth invariant: the pipeline's TIN export winding order is not
   * something this package checks, and a single-sided material silently drops
   * raycasts on any "backwards" triangle — an AC-N2 (collider continuity)
   * violation that is otherwise invisible until someone walks off the edge of a
   * hole.
   *
   * `bounds` is required and must be a valid, non-degenerate local-space AABB:
   * a missing or unusable bound is a reported blocker, never guessed from the
   * mesh geometry (hard constraint #7).
   */
  addTile(id: string, mesh: Mesh, bounds: ColliderTileBounds): void {
    assertUsableBounds(id, bounds);
    forceDoubleSided(mesh.material);

    // Duplicate id: purge the stale entry from the index first (without touching
    // generation — this single addTile call increments once, below).
    this.deindex(id);

    if (this.gridOriginX === null) {
      this.gridOriginX = bounds.minX;
      this.gridOriginZ = bounds.minZ;
    }

    const cellKeys = this.cellKeysForBounds(bounds);
    this.tiles.set(id, { mesh, cellKeys });
    for (const key of cellKeys) {
      let cell = this.grid.get(key);
      if (!cell) {
        cell = new Map<string, Mesh>();
        this.grid.set(key, cell);
      }
      cell.set(id, mesh);
    }
    // Build the BVH so the eventual raycast against this tile is O(log n) over
    // its triangles. Guarded on a position attribute: an empty geometry has
    // nothing to accelerate and MeshBVH would throw reading position.count.
    if (mesh.geometry.getAttribute('position')) {
      mesh.geometry.computeBoundsTree();
    }
    this.tilesGeneration += 1;
  }

  removeTile(id: string): Mesh | undefined {
    const entry = this.tiles.get(id);
    if (this.deindex(id)) this.tilesGeneration += 1;
    return entry?.mesh;
  }

  hasTile(id: string): boolean {
    return this.tiles.has(id);
  }

  get tileCount(): number {
    return this.tiles.size;
  }

  /**
   * Downward raycast at (x, z). Returns the highest surface hit's elevation
   * (metres AOD, i.e. the local-space y), or null if no loaded tile covers
   * this point (AC-N2: this should never be null within a fully-tiled AOI).
   *
   * Only the covering grid cell is raycast first; the 8 neighbouring cells are
   * tried solely when the covering cell yields no hit (a fringe/float miss at a
   * tile edge, or a point that half-open containment placed just across a seam).
   */
  sampleGround(x: number, z: number): number | null {
    if (this.gridOriginX === null) return null;
    this.rayOrigin.set(x, RAYCAST_ORIGIN_Y, z);
    this.raycaster.set(this.rayOrigin, DOWN);
    this.raycaster.far = RAYCAST_ORIGIN_Y + 10000;

    const cx = Math.floor((x - this.gridOriginX) / CELL_SIZE_M);
    const cz = Math.floor((z - this.gridOriginZ) / CELL_SIZE_M);
    const visited = this.visitedScratch;
    visited.clear();

    const covering = this.raycastCell(cx, cz, visited);
    if (covering !== null) return covering;

    let best: number | null = null;
    for (const [dx, dz] of NEIGHBOUR_OFFSETS) {
      const hit = this.raycastCell(cx + dx, cz + dz, visited);
      if (hit !== null && (best === null || hit > best)) best = hit;
    }
    return best;
  }

  /** True if a downward ray at (x, z) hits any loaded tile. */
  hasGroundAt(x: number, z: number): boolean {
    return this.sampleGround(x, z) !== null;
  }

  /**
   * Casts an arbitrary (non-vertical) ray — the double-click/double-tap
   * teleport pick (issue #156) — against every currently loaded tile mesh
   * and returns the nearest hit point, or null if the ray hits no terrain.
   *
   * Only ever raycasts tiles registered here, i.e. terrain meshes: buildings,
   * turbines, trees, the route ribbon and other scene furniture are never
   * added to this collider, so they cannot win this pick even if they sit
   * between the camera and the terrain. Called once per gesture (not per
   * frame), so a linear scan over loaded tiles — each still BVH-accelerated —
   * is not a render-loop cost.
   */
  raycastSurface(raycaster: Raycaster): Vector3 | null {
    let best: Intersection | null = null;
    for (const { mesh } of this.tiles.values()) {
      this.hitScratch.length = 0;
      const hits = raycaster.intersectObject(mesh, false, this.hitScratch);
      for (const hit of hits) {
        if (best === null || hit.distance < best.distance) best = hit;
      }
    }
    return best ? best.point.clone() : null;
  }

  /** Raycasts every not-yet-visited mesh in one cell; returns the highest hit or null. */
  private raycastCell(cx: number, cz: number, visited: Set<Mesh>): number | null {
    const cell = this.grid.get(`${cx},${cz}`);
    if (!cell) return null;
    let best: number | null = null;
    for (const mesh of cell.values()) {
      if (visited.has(mesh)) continue;
      visited.add(mesh);
      // intersectObject appends into (and returns) the array passed to it, so
      // the shared buffer must be emptied before every cast.
      this.hitScratch.length = 0;
      const hits = this.raycaster.intersectObject(mesh, false, this.hitScratch);
      for (const hit of hits) {
        if (best === null || hit.point.y > best) best = hit.point.y;
      }
    }
    return best;
  }

  private cellKeysForBounds(bounds: ColliderTileBounds): string[] {
    const originX = this.gridOriginX ?? bounds.minX;
    const originZ = this.gridOriginZ;
    const cxLo = Math.floor((bounds.minX - originX) / CELL_SIZE_M);
    const cxHi = Math.floor((bounds.maxX - EDGE_EPS - originX) / CELL_SIZE_M);
    const czLo = Math.floor((bounds.minZ - originZ) / CELL_SIZE_M);
    const czHi = Math.floor((bounds.maxZ - EDGE_EPS - originZ) / CELL_SIZE_M);
    const keys: string[] = [];
    for (let cx = cxLo; cx <= cxHi; cx++) {
      for (let cz = czLo; cz <= czHi; cz++) keys.push(`${cx},${cz}`);
    }
    return keys;
  }

  /** Removes a tile id from the grid index and the tile map. Returns whether it existed. */
  private deindex(id: string): boolean {
    const entry = this.tiles.get(id);
    if (!entry) return false;
    for (const key of entry.cellKeys) {
      const cell = this.grid.get(key);
      if (!cell) continue;
      cell.delete(id);
      if (cell.size === 0) this.grid.delete(key);
    }
    // Release the tile's BVH so it does not leak as tiles stream in and out
    // during camera movement (disposeBoundsTree clears geometry.boundsTree).
    if (entry.mesh.geometry.boundsTree) entry.mesh.geometry.disposeBoundsTree();
    this.tiles.delete(id);
    return true;
  }
}

function assertUsableBounds(id: string, bounds: ColliderTileBounds | undefined): void {
  if (
    !bounds ||
    !Number.isFinite(bounds.minX) ||
    !Number.isFinite(bounds.minZ) ||
    !Number.isFinite(bounds.maxX) ||
    !Number.isFinite(bounds.maxZ) ||
    bounds.maxX <= bounds.minX ||
    bounds.maxZ <= bounds.minZ
  ) {
    throw new Error(
      `TerrainCollider.addTile(${id}): missing or degenerate bounds ${JSON.stringify(
        bounds,
      )} — a missing input is a reported blocker, never guessed geometry (hard constraint #7).`,
    );
  }
}

function forceDoubleSided(material: Mesh['material']): void {
  const materials: Material[] = Array.isArray(material) ? material : [material];
  for (const m of materials) m.side = DoubleSide;
}
